import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { accommodationInfos } from '../api';
import { AuthContext } from '../context/AuthContext';
import { IAccommodationInfo } from '../utils/types';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';
import { HotelCard } from '../components/HotelCard';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';

type AccommodationContainerProps = {
  salesIndividualId: string;
};

export function AccommodationContainer({
  salesIndividualId,
}: AccommodationContainerProps) {
  const [result, setResult] = useState<
    { city: string; data: IAccommodationInfo[] }[]
  >([]);
  const { token } = useContext(AuthContext);
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [numberOfRoommates, setNumberOfRoommates] = useState<number>(0);
  const showSnackbar = useAlertSnackbar();

  useEffect(() => {
    fetchAccomodationInfos(salesIndividualId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesIndividualId]);

  const fetchAccomodationInfos = async (id: string) => {
    try {
      const res = await accommodationInfos(id, token);
      const data = await res.json();
      if (res?.status === 200) {
        const groupedData = data.reduce(
          (
            acc: {
              [key: string]: { city: string; data: IAccommodationInfo[] };
            },
            curr: IAccommodationInfo,
          ) => {
            const city = curr.city;
            if (!acc[city]) {
              acc[city] = { city, data: [] };
            }
            acc[city].data.push(curr);
            setNumberOfRoommates(
              (prev) => prev + (curr.roomGroupCustomer?.length || 0),
            );
            return acc;
          },
          {},
        );

        setResult(Object.values(groupedData));
      } else {
        throw data;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      showSnackbar(e.detail || t('loginPage.registerErrorMessage'), 'error');
    } finally {
      setLoading(false);
    }
  };
  if (loading) {
    return <CircularProgress color="secondary" />;
  }
  if (result.length === 0) {
    return (
      <Box
        sx={{
          mt: 2,
          mb: 4,
        }}
      >
        {t('accountContainer.noRecord')}{' '}
        <span style={{ textDecoration: 'underline' }}>
          {t('accountContainer.noRecordWord')}
        </span>
      </Box>
    );
  }

  return (
    <>
      {result?.map((c, i) => (
        <>
          <Typography variant="h6" gutterBottom>
            {c.city}
          </Typography>
          <Grid2 container spacing={2} sx={{ mb: 2 }}>
            {c.data.map((r) => (
              <Grid2
                xs={12}
                md={6}
                lg={4}
                key={`${r.accommodationStayName}-${i}`}
              >
                <HotelCard
                  accommodationStayId={r.accommodationStayId}
                  name={r.accommodationStayName}
                  imageUrl={r.accommodationStayImagePath}
                  stars={
                    r.accommodationStayStar
                      ? parseInt(r.accommodationStayStar)
                      : 0
                  }
                  stayNight={r.stayNight}
                  stayDays={r.stayNight + 1}
                  startDate={r.startDate}
                  endDate={r.endDate}
                  roomType={r.roomType}
                  bedType={r.bedType}
                  roomNumber={r.roomNumber}
                  latitude={r.latitude}
                  longitude={r.longitude}
                />
              </Grid2>
            ))}
          </Grid2>
        </>
      ))}
      <Typography variant="h6" gutterBottom>
        {t('accommodationContainer.roomMates')}
      </Typography>

      <Box sx={{ maxWidth: 850 }}>
        {numberOfRoommates > 0 ? (
          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('accommodationContainer.city')}</TableCell>
                  <TableCell>{t('accommodationContainer.name')}</TableCell>
                  <TableCell>{t('accommodationContainer.hotel')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result?.map((cd) =>
                  cd.data.map((r) =>
                    r.roomGroupCustomer?.map((c, i) => (
                      <TableRow
                        key={`${r.roomGroupId}-${c.name}-${c.lastName}-${i}`}
                      >
                        {i === 0 && (
                          <TableCell rowSpan={r.roomGroupCustomer?.length || 1}>
                            {r.city}
                          </TableCell>
                        )}
                        <TableCell>
                          {c.name} {c.lastName}
                        </TableCell>
                        <TableCell>{r.accommodationStayName || '-'}</TableCell>
                      </TableRow>
                    )),
                  ),
                )}
              </TableBody>
            </Table>
          </Paper>
        ) : (
          <Alert severity="warning">
            {t('accommodationContainer.noRoommates')}
          </Alert>
        )}
      </Box>
    </>
  );
}
